import React, { useState, useEffect } from 'react';
import { Grid, Checkbox, Button, Label, Segment, Tab, Table } from 'semantic-ui-react';
import ScriptEditor from './ScriptEditor';
import InputField from 'common/InputField';
import MapView, { drawIcon } from 'common/MapView';
import BlueprintChooser from 'common/BlueprintChooser';

const mapSize = function(tiles) {
  const rows = tiles.split('\n');
  const w = rows[0].length;
  const h = rows.length;
  return [w, h];
}

const MapTile = function(props) {
  const t = props.tile-32;
  const sx = t % 16;
  const sy = Math.floor(t/16);
  const tx = -(1 + sx*34);
  const ty = -(1 + sy*34);
  const extras = props.style || {};

  const style = {
    height: '32px',
    width: '32px',
    display: 'inline-block',
    verticalAlign: 'middle',
    border: 'none',
    background: ("url('terrain.png') " + tx + "px " + ty + "px"),
    ...extras
  };

  return <span onMouseEnter={props.onMouseEnter}
               onContextMenu={props.onContextMenu}
               onClick={props.onClick}
               style={style} />;
}

const toolbar = ' !"#$%&\'()*+,-./0123456789:;<>BCOPQST[\\]^`abcelnopqrstuvw|~';

const MODE_TILES = 'MODE_TILES';
const MODE_SPAWN = 'MODE_SPAWN';
const MODE_TRIGGER = 'MODE_TRIGGER';

const MapEditor = function(props) {
  const map = props.map;
  const change = props.onChange;
  const [ w, h ] = mapSize(map.Tiles);
  const [ newW, setNewW ] = useState(w.toString());
  const [ newH, setNewH ] = useState(h.toString());
  const [ tile, setTile ] = useState('#');
  const [ hover, setHover ] = useState(null);
  const [ mode, setMode ] = useState(MODE_TILES);
  const [ spawnIndex, setSpawnIndex ] = useState(-1);
  const [ triggerIndex, setTriggerIndex ] = useState(-1);
  const [ triggerCorner, setTriggerCorner ] = useState(true);
  const [ isDown, setIsDown ] = useState(false);

  useEffect(() => {
    setNewH(h.toString());
    setNewW(w.toString());
    setMode(MODE_TILES);
    setSpawnIndex(-1);
    setTriggerIndex(-1);
    setTriggerCorner(true);
  }, [map.ID, w, h]);

  const resize = () => {
    var tiles = "";
    for (var y = 0; y < newH; y++) {
        for (var x = 0; x < newW; x++) {
            if (x !== 0 && y !== 0 && x !== newW-1 && y !== newH-1)
              tiles = tiles + ".";
            else
              tiles = tiles + "#";
        }
        if (y !== newH - 1) tiles = tiles + "\n";
    }
    change('Tiles')(tiles);
  };

  const resizeValid = newH > 2 && newW > 2 && newH < 100 && newW < 100;

  const createSpawn = function() {
    let newSpawn = [...map.Spawn];
    newSpawn.push({
      ID: 'spawn',
      x: 0,
      y: 0
    });
    change('Spawn')(newSpawn);
  }

  const delSpawn = function(i) {
    change('Spawn')(map.Spawn.filter((s,idx) => idx !== i));
  }

  const createTrigger = function() {
    let newTrigger = [...map.Trigger];
    newTrigger.push({
      X1: 0,
      Y1: 0,
      X2: 0,
      Y2: 0,
      OnInteract: '',
      OnMove: '',
      OnlyPC: true,
      OnlyOnce: false
    });
    change('Trigger')(newTrigger);
  }

  const delTrigger = function(i) {
    change('Trigger')(map.Trigger.filter((t,idx) => idx !== i));
  }

  const downMap = function(tx, ty) {
    setIsDown(true);
  }

  const upMap = function(tx, ty) {
    setIsDown(false);
  }

  const clickMap = function(tx, ty) {

    if (tx < 0 || ty < 0) return;
    if (tx > w || ty > h) return;

    if (mode === MODE_TILES) {
      const i = (w+1)*ty + tx;
      const tiles = map.Tiles.substr(0, i) + tile + map.Tiles.substr(i+1);
      change('Tiles')(tiles);
    }
    else if (mode === MODE_SPAWN) {
      const newSpawn = map.Spawn.map((s,i) => {
        if (spawnIndex === i) {
          return {
              ...s,
              x: tx,
              y: ty
          };
        }

        return s;
      });
      change('Spawn')(newSpawn);
    }
    else if (mode === MODE_TRIGGER) {
      const newTrigger = map.Trigger.map((t, i) => {
        if (triggerIndex === i) {
          let trig = {...t};
          if (triggerCorner) {
            trig.X1 = tx;
          }
          else {
            trig.X2 = tx;
          }
          if (triggerCorner) {
            trig.Y1 = ty;
          }
          else {
            trig.Y2 = ty;
          }
          return trig;
        }
        return t;
      });
      setTriggerCorner(!triggerCorner);
      change('Trigger')(newTrigger);
    }
  }

  const moveMap = function(tx, ty) {
    if (tx === null) {
      setHover(null);
      setIsDown(false);
    }
    else {
      if (hover === null || hover[0] !== tx || hover[1] !== ty) {
        setHover([tx, ty]);
        if (isDown && mode === MODE_TILES) {
          clickMap(tx, ty);
        }
      }
    }
  }

  const drawOverlay = function(ctx, tx, ty) {
    map.Spawn.forEach(s => {
      if (s.x === tx && s.y === ty) {
        const spawnedBlueprint = props.blueprints.find(bp => bp.ID === s.ID);
        if (spawnedBlueprint) {
          drawIcon(ctx, spawnedBlueprint.Icon, tx, ty);
        }
        ctx.font = 'bold 16px sans-serif';
        ctx.fillStyle = 'red';
        ctx.fillText(s.ID, tx*32, ty*32 + 10);
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1.0;
        ctx.setLineDash([]);
        ctx.strokeText(s.ID, tx*32, ty*32 + 10);
      }
    });

    map.Trigger.forEach(t => {
      if (t.X1 <= tx && t.X2 >= tx &&
          t.Y1 <= ty && t.Y2 >= ty) {
        // draw triggerIndex
        ctx.strokeStyle = '#ffff00';
        ctx.lineWidth = 2.0;
        ctx.setLineDash([4, 8]);
        ctx.strokeRect(tx*32 + 1, ty*32 + 1, 30, 30);
      }
    });

    if (hover !== null && hover[0] === tx && hover[1] === ty) {
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2.0;
      ctx.setLineDash([4, 8]);
      ctx.strokeRect(tx*32 + 1, ty*32 + 1, 30, 30);
    }
  }

  const toolbarTiles = function() {
    return (
      <React.Fragment>
        <div>
          <InputField value={newW}
                      onChange={setNewW}
                      label='New width'
                      placeholder='Width'
                      type='int' />
          <InputField value={newH}
                      onChange={setNewH}
                      label='New height'
                      placeholder='Height'
                      type='int' />
          <Button disabled={!resizeValid} onClick={resize}>Resize</Button>
        </div>
        <div>
          {toolbar.split('').map((ign, i) => {
            const t = toolbar.charCodeAt(i);
            const style = {};
            if (ign === tile)
              style.border = '2px solid red';
            return <MapTile style={style} key={i} tile={t} onClick={() => setTile(ign)} />
          })}
        </div>
      </React.Fragment>
    );
  }

  const spawnableTypes = ['Creature', 'Trap'];
  const spawnable = props.blueprints.filter(bp => spawnableTypes.includes(bp._type));

  const toolbarSpawn = function() {
    return (
      <React.Fragment>
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Spawn blueprint ID</Table.HeaderCell>
              <Table.HeaderCell>Spawn at X</Table.HeaderCell>
              <Table.HeaderCell>Spawn at Y</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {map.Spawn.map((s, i) => {
              const changeSpawn = function(f) {
                return function(val) {
                  var thisSpawn = {...s};
                  var newSpawn = [...map.Spawn];
                  thisSpawn[f] = val;
                  newSpawn[i] = thisSpawn;
                  change('Spawn')(newSpawn);
                };
              };

              const moveSpawnMode = function() {
                if (spawnIndex === i) {
                  setSpawnIndex(-1);
                  setTriggerIndex(-1);
                  setMode(MODE_TILES);
                }
                else {
                  setSpawnIndex(i);
                  setTriggerIndex(-1);
                  setMode(MODE_SPAWN);
                }

              };

              return (
                <Table.Row key={i}>
                  <Table.Cell>
                    <BlueprintChooser blueprints={spawnable} selected={s.ID} onChange={changeSpawn('ID')} />
                    <Button icon='delete' color='red' onClick={() => delSpawn(i)} />
                    { spawnIndex === i  ? <Button icon='cancel' content='Cancel' color='red' onClick={moveSpawnMode} />
                                          : <Button icon='map marker' content='Change pos' onClick={moveSpawnMode} /> }
                  </Table.Cell>
                  <Table.Cell>{s.x}</Table.Cell>
                  <Table.Cell>{s.y}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
        <Button icon='add' content='Add' onClick={createSpawn} />
      </React.Fragment>
    );
  }

  const toolbarTrigger = function() {
    return (
      <React.Fragment>
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={2}>Area</Table.HeaderCell>
              <Table.HeaderCell width={1}>OnlyPC</Table.HeaderCell>
              <Table.HeaderCell width={1}>OnlyOnce</Table.HeaderCell>
              <Table.HeaderCell width={6}>OnInteract</Table.HeaderCell>
              <Table.HeaderCell width={6}>OnMove</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {map.Trigger.map((t, i) => {

              const changeTrigger = function(f) {
                return function(val) {
                  var thisTrigger = {...t};
                  var newTrigger = [...map.Trigger];
                  thisTrigger[f] = val;
                  newTrigger[i] = thisTrigger;
                  change('Trigger')(newTrigger);
                };
              };

              const moveTriggerMode = function() {
                if (triggerIndex === i) {
                  setTriggerIndex(-1);
                  setSpawnIndex(-1);
                  setMode(MODE_TILES);
                }
                else {
                  setTriggerIndex(i);
                  setSpawnIndex(-1);
                  setTriggerCorner(true);
                  setMode(MODE_TRIGGER);
                }

              };

              return (
                <Table.Row key={i}>
                  <Table.Cell>
                    <Button icon='delete' color='red' onClick={() => delTrigger(i)} />
                    <span className="coords">({t.X1},{t.Y1}) - ({t.X2},{t.Y2})</span>
                    { triggerIndex === i  ? <Button icon='cancel' content='Cancel' color='red' onClick={moveTriggerMode} />
                                          : <Button icon='map marker' content='Change pos' onClick={moveTriggerMode} /> }
                  </Table.Cell>
                  <Table.Cell>
                    <Checkbox checked={t.OnlyPC}
                              onChange={() => changeTrigger('OnlyPC')(!t.OnlyPC)} />
                  </Table.Cell>
                  <Table.Cell>
                    <Checkbox checked={t.OnlyOnce}
                              onChange={() => changeTrigger('OnlyOnce')(!t.OnlyOnce)} />
                  </Table.Cell>
                  <Table.Cell>
                    <ScriptEditor src={t.OnInteract}
                                  onSave={changeTrigger('OnInteract')} />
                  </Table.Cell>
                  <Table.Cell>
                    <ScriptEditor src={t.OnMove}
                                  onSave={changeTrigger('OnMove')} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
        <Button icon='add' content='Add' onClick={createTrigger} />
      </React.Fragment>
    );
  }

  const panes = [
    {
      menuItem: { key: 'tiles', icon: 'map', content: 'Tiles' },
      render: toolbarTiles
    },
    {
      menuItem: { key: 'spawn', icon: 'map marker', content: 'Spawn' },
      render: toolbarSpawn
    },
    {
      menuItem: { key: 'trigger', icon: 'th', content: 'Triggers'},
      render: toolbarTrigger
    }
  ];

  return (
    <React.Fragment>
      <Grid.Row>
        <Grid.Column width={2}>
          <Checkbox checked={map.Outdoor}
                    onChange={() => change('Outdoor')(!map.Outdoor)}
                    label='Outdoor' />
        </Grid.Column>
        <Grid.Column width={7}>
          <Label>OnMove</Label>
          <ScriptEditor src={map.OnMove}
                        onSave={change('OnMove')} />
        </Grid.Column>
        <Grid.Column width={7}>
          <Label>OnInteract</Label>
          <ScriptEditor src={map.OnInteract}
                        onSave={change('OnInteract')} />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>
          <Segment>
            <MapView map={map} onUp={upMap} onDown={downMap} onClick={clickMap} onMove={moveMap} onDrawTile={drawOverlay} />
          </Segment>
          <Tab panes={panes} />
        </Grid.Column>
      </Grid.Row>
    </React.Fragment>
  );
}


export default MapEditor;
